import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Icon from './Icon';
import { getAccessToken, isSecretaryContextUser } from '../authStorage';
import { fetchBoardMembers } from '../boardApi';
import { fetchDecisions } from '../decisionsApi';
import { fetchMeetings } from '../meetingsApi';
import { fetchBranches } from '../branchesApi';
import { createTask, fetchTaskById, mapTaskFormToPayload, updateTask } from '../tasksApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './DecisionsPage.css';
import './DecisionFormPage.css';

export default function TaskFormPage({ mode }) {
  const { direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { id: idParam } = useParams();
  const basePath = location.pathname.startsWith('/officer') ? '/officer' : '/app';
  const isNew = mode === 'new';
  const editId = isNew ? null : Number(idParam);
  const linkedMeetingQueryId = searchParams.get('meetingId');

  const [branches, setBranches] = useState([]);
  const [members, setMembers] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: '', description: '', companyId: '', assigneeBoardMemberId: '', dueDateUtc: '',
    priority: 'medium', status: 'new', linkedMeetingId: '', linkedDecisionId: '',
  });
  const [errs, setErrs] = useState({});

  useEffect(() => {
    if (!getAccessToken()) { navigate('/login', { replace: true }); return; }
    if (!isSecretaryContextUser()) { setError(t('common.noPerm')); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const [b, m, mt, d] = await Promise.all([fetchBranches(), fetchBoardMembers(), fetchMeetings(), fetchDecisions()]);
        if (cancelled) return;
        setBranches(b);
        setMembers(m);
        setMeetings(mt);
        setDecisions(d);
        const firstCompanyId = b[0]?.id ? String(b[0].id) : '';
        if (isNew) {
          const qMeeting = linkedMeetingQueryId ? mt.find((x) => String(x.id) === String(linkedMeetingQueryId)) : null;
          setForm((p) => ({ ...p, companyId: qMeeting ? String(qMeeting.companyId) : firstCompanyId, linkedMeetingId: qMeeting ? String(qMeeting.id) : '' }));
        } else if (editId && Number.isFinite(editId)) {
          const row = await fetchTaskById(editId);
          if (cancelled) return;
          setForm({
            title: row.title || '',
            description: row.description || '',
            companyId: String(row.companyId || ''),
            assigneeBoardMemberId: row.assigneeBoardMemberId ? String(row.assigneeBoardMemberId) : '',
            dueDateUtc: row.dueDateUtc ? String(row.dueDateUtc).slice(0, 10) : '',
            priority: row.priority || 'medium',
            status: row.status === 'overdue' ? 'in_progress' : (row.status || 'new'),
            linkedMeetingId: row.linkedMeetingId ? String(row.linkedMeetingId) : '',
            linkedDecisionId: row.linkedDecisionId ? String(row.linkedDecisionId) : '',
          });
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('tasks.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, isNew, editId, linkedMeetingQueryId]); // eslint-disable-line react-hooks/exhaustive-deps

  const validate = () => {
    const e = {};
    if (!String(form.title || '').trim()) e.title = t('tasks.valTitle');
    if (!String(form.companyId || '').trim()) e.companyId = t('departments.valBranch');
    setErrs(e);
    return Object.keys(e).length === 0;
  };

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    setError('');
    try {
      const payload = mapTaskFormToPayload(form);
      if (isNew) {
        const created = await createTask(payload);
        navigate(`${basePath}/tasks/${created.id}`);
      } else {
        const updated = await updateTask(editId, payload);
        navigate(`${basePath}/tasks/${updated.id}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : t('tasks.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="decision-v2" dir={direction}>
      <div className="page-wrapper">
      {error && <div className="v2-error">{error}</div>}
      {loading && <div className="v2-loading">{t('common.loading')}</div>}
      {!loading && (
        <div className="card">
          <div className="card-top">
            <div className="top-bar top-bar--single-line">
              <div className="page-title-row">
                <h1 className="page-title">
                  <span className="page-title-icon"><Icon name="settings" size={16} color="#0EA5A0" /></span>
                  {isNew ? t('tasks.titleNew') : t('tasks.titleEdit')}
                </h1>
              </div>
              <div className="action-btns">
                <button type="button" className="btn btn-ghost" onClick={() => navigate(`${basePath}/tasks`)}>{t('common.cancel')}</button>
                <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving ? t('tasks.saving') : t('common.save')}</button>
              </div>
            </div>
          </div>
          <div className="card-inner">
            <div className="fields-grid">
                <div className="field-group">
                  <label className="field-label"><Icon name="settings" size={14} color="#0EA5A0" /> {t('tasks.lblTitle')} <span className="required">*</span></label>
                  <div className="field-input">
                    <input className={errs.title ? 'mform__input--error' : ''} value={form.title} placeholder={t('tasks.lblTitle')} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
                  </div>
                  {errs.title && <span className="mform__error">{errs.title}</span>}
                </div>
                <div className="field-group">
                  <label className="field-label"><Icon name="branch" size={14} color="#0EA5A0" /> {t('common.company')} <span className="required">*</span></label>
                  <div className={`field-input select-wrapper${errs.companyId ? ' mform__select--error' : ''}`}>
                    <select value={form.companyId} onChange={(e) => setForm((p) => ({ ...p, companyId: e.target.value }))}>
                      <option value="">{t('board.pickBranch')}</option>
                      {branches.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                    </select>
                  </div>
                  {errs.companyId && <span className="mform__error">{errs.companyId}</span>}
                </div>
                <div className="field-group span-2">
                  <label className="field-label"><Icon name="edit" size={14} color="#0EA5A0" /> {t('tasks.lblDesc')}</label>
                  <div className="field-input"><input value={form.description} placeholder={t('tasks.lblDesc')} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} /></div>
                </div>
                <div className="field-group">
                  <label className="field-label"><Icon name="user" size={14} color="#0EA5A0" /> {t('tasks.lblAssignee')}</label>
                  <div className="field-input select-wrapper"><select value={form.assigneeBoardMemberId} onChange={(e) => setForm((p) => ({ ...p, assigneeBoardMemberId: e.target.value }))}><option value="">—</option>{members.filter((x) => String(x.branchId) === String(form.companyId)).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></div>
                </div>
                <div className="field-group">
                  <label className="field-label"><Icon name="calendar" size={14} color="#0EA5A0" /> {t('tasks.lblDueDate')}</label>
                  <div className="field-input"><input type="date" value={form.dueDateUtc} onChange={(e) => setForm((p) => ({ ...p, dueDateUtc: e.target.value }))} /></div>
                </div>
                <div className="field-group">
                  <label className="field-label"><Icon name="chart" size={14} color="#0EA5A0" /> {t('tasks.lblPriority')}</label>
                  <div className="field-input select-wrapper"><select value={form.priority} onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}><option value="high">{t('tasks.priorityHigh')}</option><option value="medium">{t('tasks.priorityMedium')}</option><option value="low">{t('tasks.priorityLow')}</option></select></div>
                </div>
                <div className="field-group">
                  <label className="field-label"><Icon name="settings" size={14} color="#0EA5A0" /> {t('tasks.lblStatus')}</label>
                  <div className="field-input select-wrapper"><select value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}><option value="new">{t('tasks.statusNew')}</option><option value="in_progress">{t('tasks.statusInProgress')}</option><option value="completed">{t('tasks.statusCompleted')}</option></select></div>
                </div>
                <div className="field-group">
                  <label className="field-label"><Icon name="calendar" size={14} color="#0EA5A0" /> {t('tasks.lblLinkedMeeting')}</label>
                  <div className="field-input select-wrapper"><select value={form.linkedMeetingId} onChange={(e) => setForm((p) => ({ ...p, linkedMeetingId: e.target.value, linkedDecisionId: e.target.value ? '' : p.linkedDecisionId }))}><option value="">—</option>{meetings.filter((x) => String(x.companyId) === String(form.companyId)).map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}</select></div>
                </div>
                <div className="field-group">
                  <label className="field-label"><Icon name="money" size={14} color="#0EA5A0" /> {t('tasks.lblLinkedDecision')}</label>
                  <div className="field-input select-wrapper"><select value={form.linkedDecisionId} onChange={(e) => setForm((p) => ({ ...p, linkedDecisionId: e.target.value, linkedMeetingId: e.target.value ? '' : p.linkedMeetingId }))}><option value="">—</option>{decisions.filter((x) => String(x.branchId) === String(form.companyId)).map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}</select></div>
                </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
